import { Tab, TabList, Divider } from "@fluentui/react-components";
import { useState, useCallback, useRef, useMemo } from "react";
import { ArrowClockwise24Filled } from "@fluentui/react-icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  AttachRegular,
  CheckmarkCircle24Filled,
  Delete24Regular,
  Eye24Regular,
  MoreVerticalRegular,
} from "@fluentui/react-icons";
import {
  Button,
  makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Skeleton,
  SkeletonItem,
  Tooltip,
  typographyStyles,
} from "@fluentui/react-components";
import moment from "moment";
import { getTimeDifference } from "../../../utils";
import {
  fetchAllDocumentMessage,
  patchMessage,
  deleteMessage,
} from "../../../redux/clientPortal/clientPortalslice";
import ViewMessage from "./viewMessage";
import PortalDeletePopup from "../../../components/DeletePopup/portalDeletePopup";
import { MessageItem } from "../../../types/portal";
import "../style.css";

enum TabValues {
  tab1 = "received",
  tab2 = "sent",
}

const useStyles = makeStyles({
  text: { ...typographyStyles.subtitle2, color: "red" },
  name: { ...typographyStyles.subtitle1 },
  subject: {
    ...typographyStyles.subtitle2,
    whiteSpace: "nowrap",
  },
  message: { ...typographyStyles.body1 },
});

function ReceivedMessages({ notify, pagination }: any) {
  const styles = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const { userId } = useSelector((state: RootState) => state.authUser);
  const { message, loading, error } = useSelector(
    (state: RootState) => state.portal
  );
  const receivedMessages = message.received;

  const [openMessage, setOpenMessage] = useState<boolean>(false);
  const [currentMessageId, setCurrentMessageId] = useState<string | null>(null);
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const [messageDetails, setMessageDetails] = useState({});
  const [actionProcessing, setActionProcessing] = useState(false);

  const handleView = useCallback(
    (messageId: string, msg: MessageItem) => {
      if (messageId) {
        setMessageDetails({
          ...msg,
        });
        setOpenMessage(true);

        if (!msg.Read) {
          dispatch(patchMessage(messageId))
            .unwrap()
            .then(() => {
              if (userId) {
                dispatch(
                  fetchAllDocumentMessage({
                    userId,
                    page: pagination.page,
                    limit: pagination.limit,
                  })
                );
              }
            })
            .catch((err) => {
              console.error("Error updating message:", err);
            });
        }
      }
    },
    [dispatch, userId]
  );

  const handleDeleteClick = useCallback((messageId: string) => {
    setCurrentMessageId(messageId);
    setDeletePopupOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    setActionProcessing(true);
    if (currentMessageId) {
      dispatch(deleteMessage(currentMessageId))
        .then((res) => {
          notify(res.payload.message, "", "success");
          if (userId) {
            dispatch(
              fetchAllDocumentMessage({
                userId,
                page: pagination.page,
                limit: pagination.limit,
              })
            );
          }
          setActionProcessing(false);
        })
        .catch((err) => {
          console.error("Error deleting message:", err);
          setActionProcessing(false);
        });
      setDeletePopupOpen(false);
      setCurrentMessageId(null);
    }
  }, [dispatch, userId, currentMessageId]);

  return (
    <>
      {loading ? (
        Array.from({ length: 5 }).map((__, index) => (
          <div key={index} className="invertedWrapper">
            <Skeleton style={{ width: "100%" }} appearance="translucent">
              <SkeletonItem
                style={{ width: "15%", height: "20px", margin: "5px 0" }}
              />
              <SkeletonItem
                style={{ width: "100%", height: "20px", margin: "5px 0" }}
              />
              <SkeletonItem
                style={{ width: "40%", height: "20px", margin: "5px 0" }}
              />
            </Skeleton>
          </div>
        ))
      ) : receivedMessages?.length ? (
        receivedMessages.map((msg: MessageItem) => {
          const formattedTooltip = moment(msg.CreatedAt).format(
            "DD/MM/YYYY HH:mm a"
          );
          const relativeTime = getTimeDifference(msg.CreatedAt);
          return (
            <div
              key={msg.MessageId}
              onClick={() => handleView(msg.MessageId, msg)}
            >
              <div
                className="msg_box"
                style={{ color: msg.Read ? "#000000a6" : "#000" }}
              >
                <div className="w-full">
                  <div className="justify_between">
                    <p className={styles.name}>
                      {msg.customer?.LastName} {msg.customer?.FirstNames}
                    </p>
                    <Tooltip
                      withArrow
                      appearance="inverted"
                      positioning="below"
                      content={formattedTooltip}
                      relationship="label"
                    >
                      <p className={styles.message}>{relativeTime}</p>
                    </Tooltip>
                  </div>
                  <div>
                    <p className={`${styles.subject} lineClamp`}>
                      {msg.Subject}
                    </p>

                    <p className={`${styles.message} lineClamp`}>
                      {msg.Message}
                    </p>
                  </div>
                </div>
                <div className="justify_between">
                  {msg.attachments && msg.attachments.length > 0 && (
                    <AttachRegular fontSize="24px" />
                  )}
                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <Button
                        appearance="subtle"
                        icon={<MoreVerticalRegular />}
                        onClick={(event: React.MouseEvent) => {
                          event.stopPropagation();
                          setCurrentMessageId(msg.MessageId);
                        }}
                      />
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem
                          icon={<Eye24Regular />}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleView(msg.MessageId, msg);
                          }}
                        >
                          View
                        </MenuItem>

                        {/* <MenuItem

                          icon={<Delete24Regular />}
                          onClick={(event: React.MouseEvent) => {
                            event.stopPropagation();
                            handleDeleteClick(msg.MessageId);
                          }}
                        >
                          Delete

                        </MenuItem> */}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="portal_body no_data">
          <p className={styles.text}>There are no messages</p>
        </div>
      )}
      <ViewMessage
        open={openMessage}
        onClose={() => setOpenMessage(false)}
        messageDetails={messageDetails}
      />
      <PortalDeletePopup
        open={deletePopupOpen}
        onClose={() => setDeletePopupOpen(false)}
        onConfirmDelete={handleConfirmDelete}
        actionProcessing={actionProcessing}
      />
    </>
  );
}

function SentMessages({ notify, pagination }: any) {
  const styles = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const { userId } = useSelector((state: RootState) => state.authUser);
  const { message, loading, error } = useSelector(
    (state: RootState) => state.portal
  );
  const sentMessages = message.sent;
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const [openMessage, setOpenMessage] = useState<boolean>(false);
  const [currentMessageId, setCurrentMessageId] = useState<string | null>(null);
  const [messageDetails, setMessageDetails] = useState({});
  const [actionProcessing, setActionProcessing] = useState(false);

  const handleView = useCallback((messageId: string, msg: MessageItem) => {
    if (messageId) {
      setMessageDetails({
        ...msg,
      });
      setOpenMessage(true);
    }
  }, []);

  const handleDeleteClick = useCallback((messageId: string) => {
    setCurrentMessageId(messageId);
    setDeletePopupOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    setActionProcessing(true);

    if (currentMessageId) {
      dispatch(deleteMessage(currentMessageId))
        .then((res) => {
          notify(res.payload.message, "", "success");
          if (userId) {
            dispatch(
              fetchAllDocumentMessage({
                userId,
                page: pagination.page,
                limit: pagination.limit,
              })
            );
          }
          setActionProcessing(false);
        })
        .catch((err) => {
          console.error("Error deleting message:", err);
          setActionProcessing(false);
        });
      setDeletePopupOpen(false);
      setCurrentMessageId(null);
    }
  }, [dispatch, userId, currentMessageId]);

  return (
    <>
      {loading ? (
        Array.from({ length: 5 }).map((__, index) => (
          <div key={index} className="invertedWrapper">
            <Skeleton style={{ width: "100%" }} appearance="translucent">
              <SkeletonItem
                style={{ width: "15%", height: "20px", margin: "5px 0" }}
              />
              <SkeletonItem
                style={{ width: "100%", height: "20px", margin: "5px 0" }}
              />
              <SkeletonItem
                style={{ width: "40%", height: "20px", margin: "5px 0" }}
              />
            </Skeleton>
          </div>
        ))
      ) : sentMessages?.length ? (
        sentMessages.map((msg: MessageItem) => {
          const formattedTooltip = moment(msg.CreatedAt).format(
            "DD/MM/YYYY HH:mm a"
          );
          const relativeTime = getTimeDifference(msg.CreatedAt);
          const readTime = msg.ReadAt
            ? moment(msg.ReadAt).format("DD/MM/YYYY HH:mm a")
            : "";

          return (
            <div
              key={msg.MessageId}
              onClick={() => handleView(msg.MessageId, msg)}
            >
              <div className="msg_box" style={{ color: "#000000a6" }}>
                <div className="w-full">
                  <div className="justify_between">
                    <p className={styles.name}>
                      {msg.customer?.LastName} {msg.customer?.FirstNames}
                    </p>
                    <Tooltip
                      withArrow
                      appearance="inverted"
                      positioning="below"
                      content={formattedTooltip}
                      relationship="label"
                    >
                      <p className={styles.message}>{relativeTime}</p>
                    </Tooltip>
                  </div>
                  <div>
                    <p className={`${styles.subject} lineClamp`}>
                      {msg.Subject}
                    </p>

                    <p className={`${styles.message} lineClamp`}>
                      {msg.Message}
                    </p>
                  </div>
                </div>
                <div className="justify_between">
                  {msg.Read ? (
                    <Tooltip
                      withArrow
                      appearance="inverted"
                      positioning="below"
                      content={msg.Read && `Read at: ${readTime}`}
                      relationship="label"
                    >
                      <CheckmarkCircle24Filled style={{ color: "#205393" }} />
                    </Tooltip>
                  ) : (
                    <CheckmarkCircle24Filled style={{ color: "#000000a6" }} />
                  )}
                  {msg.attachments && msg.attachments.length > 0 && (
                    <AttachRegular fontSize="24px" />
                  )}
                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <Button
                        appearance="subtle"
                        icon={<MoreVerticalRegular />}
                        onClick={(event: React.MouseEvent) => {
                          event.stopPropagation();
                          setCurrentMessageId(msg.MessageId);
                        }}
                      />
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem
                          icon={<Eye24Regular />}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleView(msg.MessageId, msg);
                          }}
                        >
                          View
                        </MenuItem>
                        {/* <MenuItem

                          icon={<Delete24Regular />}
                          onClick={(event: React.MouseEvent) => {
                            event.stopPropagation();
                            handleDeleteClick(msg.MessageId);
                          }}
                        >
                          Delete
                        </MenuItem> */}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="portal_body no_data">
          <p className={styles.text}>There are no messages</p>
        </div>
      )}
      <ViewMessage
        open={openMessage}
        onClose={() => setOpenMessage(false)}
        messageDetails={messageDetails}
      />
      <PortalDeletePopup
        open={deletePopupOpen}
        onClose={() => setDeletePopupOpen(false)}
        onConfirmDelete={handleConfirmDelete}
        actionProcessing={actionProcessing}
      />
    </>
  );
}

function Messages({
  notify,
  setPagination,
  totalFetchedMessages,
  pagination,
}: any) {
  const [isFetching, setIsFetching] = useState(false);
  const portalBodyRef = useRef<HTMLDivElement | null>(null);
  const { userId } = useSelector((state: RootState) => state.authUser);
  const { message, fetched_pagination } = useSelector(
    (state: RootState) => state.portal
  );
  const dispatch = useDispatch<AppDispatch>();

  const unreadMessagesCount = useMemo(() => {
    return message.received.filter((msg) => !msg.Read).length;
  }, [message.received]);

  const [selectedTab, setSelectedTab] = useState<TabValues>(TabValues.tab1);

  const handleScroll = () => {
    const el = portalBodyRef.current;
    if (!el || isFetching) return;

    const isAtBottom =
      Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;

    if (isAtBottom && totalFetchedMessages < fetched_pagination.totalCount) {
      setIsFetching(true);
      setPagination((prev: any) => ({ ...prev, limit: prev.limit + 30 }));
      setTimeout(() => setIsFetching(false), 500);
    }
  };

  const handleRefresh = useCallback(() => {
    dispatch(
      fetchAllDocumentMessage({
        userId,
        page: pagination.page,
        limit: pagination.limit,
      })
    );
    // setPagination({ page: 1, limit: 20 });
  }, [userId]);

  return (
    <>
      <div className="portal_card">
        <div className="portal_card_header">
          <div className="justify_between">
            <div className="portal_heading">
              <h5 className="grey_bold_text">Messages</h5>
              <p className="unread_count">{unreadMessagesCount.toString()}</p>
              <ArrowClockwise24Filled
                onClick={handleRefresh}
                style={{ cursor: "pointer" }}
              />
            </div>
          </div>
          <TabList
            defaultSelectedValue={TabValues.tab1}
            onTabSelect={(_event, data) =>
              setSelectedTab(data.value as TabValues)
            }
          >
            <Tab value={TabValues.tab1}>Received</Tab>
            <Tab value={TabValues.tab2}>Sent</Tab>
          </TabList>
        </div>
        <Divider />
        <div
          className="portal_body"
          ref={portalBodyRef}
          onScroll={handleScroll}
        >
          {selectedTab === TabValues.tab1 && (
            <ReceivedMessages notify={notify} pagination={pagination} />
          )}
          {selectedTab === TabValues.tab2 && (
            <SentMessages notify={notify} pagination={pagination} />
          )}
        </div>
      </div>
    </>
  );
}

export default Messages;
